// src/lib/useSavedJobs.ts

import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

const API =
  process.env.REACT_APP_API ?? "http://localhost:8000";

export function useSavedJobs() {
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [savingId, setSavingId] = useState<string | null>(null);

  async function getToken() {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }

  async function load() {
    try {
      const token = await getToken();

      if (!token) {
        setSaved(new Set());
        return;
      }

      const response = await fetch(`${API}/api/saved-jobs`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const raw = await response.text();
        console.error("[saved jobs] load failed:", response.status, raw);
        return;
      }

      const data = await response.json();

      const urls = Array.isArray(data)
        ? data
            .map((job: any) => job.url)
            .filter((url: any): url is string => typeof url === "string")
        : [];

      setSaved(new Set(urls));
    } catch (error) {
      console.error("[saved jobs] load error:", error);
    }
  }

  useEffect(() => {
    void load();

    const { data: sub } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!session) {
          setSaved(new Set());
        } else {
          void load();
        }
      }
    );

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  async function save(job: {
    url: string;
    title?: string;
    company?: string;
    location?: string;
    jd_text?: string;
    source_code?: string;
  }) {
    const token = await getToken();

    if (!token) {
      alert("Please sign in to save jobs.");
      return;
    }

    try {
      setSavingId(job.url);

      const response = await fetch(`${API}/api/saved-jobs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(job),
      });

      const raw = await response.text();

      if (!response.ok) {
        console.error(
          "[saved jobs] save failed:",
          response.status,
          raw
        );
        return;
      }

      setSaved((prev) => {
        const next = new Set(prev);
        next.add(job.url);
        return next;
      });

      window.dispatchEvent(
        new Event("savedjobs:changed")
      );
    } catch (error) {
      console.error("[saved jobs] save error:", error);
    } finally {
      setSavingId(null);
    }
  }

  async function unsave(jobUrl: string) {
    const token = await getToken();

    if (!token) return;

    try {
      setSavingId(jobUrl);

      const response = await fetch(
        `${API}/api/saved-jobs`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            url: jobUrl,
          }),
        }
      );

      const raw = await response.text();

      if (!response.ok) {
        console.error(
          "[saved jobs] remove failed:",
          response.status,
          raw
        );
        return;
      }

      setSaved((prev) => {
        const next = new Set(prev);
        next.delete(jobUrl);
        return next;
      });

      window.dispatchEvent(
        new Event("savedjobs:changed")
      );
    } catch (error) {
      console.error(
        "[saved jobs] remove error:",
        error
      );
    } finally {
      setSavingId(null);
    }
  }
  
  return {
    saved,
    savingId,
    save,
    unsave,
  };
} 